import { gql, useMutation, useQuery } from '@apollo/client';
import {
  TRANSLATED_STRING_FIELDS,
  translateBackend,
} from '@assets/i18n/config';
import { NotificationChannelTypes } from '@interfaces/notificationGroup';

import { Badge } from '@mui/material';
import { Chip } from '@ovComponents/2-component/chip/chip';

import { Menu } from '@ovComponents/2-component/menu/menu';
import { Pagination } from '@ovComponents/2-component/pagination/pagination';
import { SelectionChip } from '@ovComponents/3-pattern/selectionChip/selectionChip';
import { UserContext } from '@providers/userContextProvider';
import { useLocalization } from '@utils/useLocalization';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Color } from 'hephaestus/core';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Skeleton,
  Typography,
  useTheme,
} from 'hephaestus/web';
import { NotificationsNone as NB } from 'hephaestus/webIcons';
import { groupBy } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

dayjs.extend(relativeTime);

const NOTIFICATIONS = `#graphql
  notifications {
    id
    read
    title { ${TRANSLATED_STRING_FIELDS} }
    body { ${TRANSLATED_STRING_FIELDS} }
    linkText { ${TRANSLATED_STRING_FIELDS} }
    link
    createdAt
  }
`;

const FETCH_UNREAD_NOTIFICATIONS = gql`
  query fetchNotifications($input: FetchNotificationsInput!) {
    fetchNotifications(input: $input) {
      ${NOTIFICATIONS}
      totalCount
    }
  }
`;

const FETCH_NOTIFICATIONS = gql`
  query fetchNotifications($input: FetchNotificationsInput!) {
    fetchNotifications(input: $input) {
      ${NOTIFICATIONS}
      totalCount
    }
  }
`;

const MARK_READ = gql`
  mutation markNotificationAsRead($input: MarkNotificationAsReadInput!) {
    markNotificationAsRead(input: $input)
  }
`;

const FETCH_CUSTODIAN_NOTIFICATIONS = gql`
  query fetchCustodianNotifications($input: FetchCustodianNotificationsInput!) {
    fetchCustodianNotifications(input: $input){
      totalCount
      ${NOTIFICATIONS}
    }
  }
`;

const MARK_ALL_READ = gql`
  mutation markAllNotificationsAsRead {
    markAllNotificationsAsRead
  }
`;

const PAGE_SIZE = 5;

enum NotificationTabType {
  ALL = 'ALL',
  UNREAD = 'UNREAD',
}

export const Notifications = ({ color }: { color?: string }) => {
  const theme = useTheme();
  const [groupedNotifications, setGroupedNotifications] = useState<any>({});
  const [notificationTab, setNotificationTab] = useState<NotificationTabType>(
    NotificationTabType.ALL,
  );
  const [totalCount, setTotalCount] = useState(0);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { t } = useTranslation('shared');
  const { localizedDate } = useLocalization();
  const { custodianConnection, activeEntity } = useContext(UserContext);

  const { userId: paramsUserId } = useParams();

  const userId = paramsUserId ?? activeEntity?.id;

  const useCustodianNotifications = !!(
    userId && custodianConnection?.enableFetchCustodianNotifications
  );

  const pagination = {
    perPage: PAGE_SIZE,
    offSet: (page - 1) * PAGE_SIZE,
    sortField: 'createdAt',
    sortDesc: true,
  };

  const baseFilter = {
    notificationChannelTypes: [NotificationChannelTypes.IN_APP],
  };

  const { data, refetch, loading } = useQuery(FETCH_UNREAD_NOTIFICATIONS, {
    variables: {
      input: {
        filter: { ...baseFilter, read: false },
        pagination,
      },
    },
    skip: useCustodianNotifications,
  });

  const {
    data: notificationsData,
    refetch: notificationsRefetch,
    loading: notificationLoading,
  } = useQuery(FETCH_NOTIFICATIONS, {
    variables: {
      input: {
        filter: { ...baseFilter },
        pagination,
      },
    },
    skip: useCustodianNotifications,
  });

  const {
    data: custodianNotificationsData,
    loading: custodianNotificationLoading,
  } = useQuery(FETCH_CUSTODIAN_NOTIFICATIONS, {
    fetchPolicy: 'cache-first',
    variables: {
      input: {
        filter: { ...baseFilter, userId },
        pagination,
      },
    },
    skip: !useCustodianNotifications,
  });

  useEffect(() => {
    let notifications: any;
    let newTotalCount = 0;
    let momentLoading = false;
    let newUnreadTotal = 0;
    if (useCustodianNotifications) {
      notifications =
        custodianNotificationsData?.fetchCustodianNotifications
          ?.notifications ?? [];
      newTotalCount =
        custodianNotificationsData?.fetchCustodianNotifications?.totalCount ??
        0;
      momentLoading = custodianNotificationLoading;
    } else if (
      notificationTab === NotificationTabType.ALL &&
      !useCustodianNotifications
    ) {
      notifications =
        notificationsData?.fetchNotifications?.notifications ?? [];
      newTotalCount = notificationsData?.fetchNotifications?.totalCount ?? 0;
      momentLoading = notificationLoading;
    } else {
      notifications = data?.fetchNotifications?.notifications ?? [];
      newTotalCount = data?.fetchNotifications?.totalCount ?? 0;
      momentLoading = loading;
    }

    if (useCustodianNotifications) {
      newUnreadTotal =
        custodianNotificationsData?.fetchCustodianNotifications?.totalCount ??
        0;
    } else {
      newUnreadTotal = data?.fetchNotifications?.totalCount ?? 0;
    }

    const notificationsToGroup = groupBy(notifications, (notification) => {
      const createdAt = localizedDate(notification.createdAt);
      const now = localizedDate(dayjs());
      return createdAt === now ? 'today' : createdAt;
    });

    const lowerBound = (page - 1) * PAGE_SIZE + 1;

    if (newTotalCount < lowerBound && !momentLoading) setPage(1);

    setGroupedNotifications(notificationsToGroup);
    setTotalCount(newTotalCount);
    setUnreadTotal(newUnreadTotal);
  }, [
    notificationTab,
    data,
    notificationsData,
    custodianNotificationsData,
    useCustodianNotifications,
  ]);

  const [markRead] = useMutation(MARK_READ);
  const [markAllRead] = useMutation(MARK_ALL_READ);

  const handleClick = (event: any) => {
    if (!useCustodianNotifications) {
      notificationsRefetch();
      refetch();
    }
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    if (!useCustodianNotifications) {
      notificationsRefetch();
      refetch();
    }
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        aria-label={t('notificationsMenu.title')}
        size='small'
        sx={{
          color,
          ...(color && {
            '&:hover': {
              backgroundColor: Color(color).alpha(0.08).string(),
            },
            '&:active': {
              backgroundColor: Color(color).alpha(0.16).string(),
            },
            '&:focus-visible': {
              backgroundColor: Color(color).alpha(0.16).string(),
            },
          }),
        }}
        onClick={handleClick}
      >
        <Badge
          invisible={unreadTotal === 0}
          variant='dot'
          color='error'
          sx={{
            // Figma Badge/Notification: 6px
            '& .MuiBadge-dot': {
              width: 6,
              height: 6,
              minWidth: 6,
              borderRadius: '100px',
            },
          }}
        >
          <NB fontSize='inherit' />
        </Badge>
      </IconButton>
      <Menu
        id='notification-menu'
        anchorEl={anchorEl}
        open={open}
        disableScrollLock={true}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { width: '350px', py: 1 },
        }}
        MenuListProps={{
          sx: { pt: 0 },
        }}
      >
        <Box sx={{ px: 2, pt: 1 }}>
          <Typography variant='headingSmall' component='div'>
            {t('notificationsMenu.title')}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            p: 2,
          }}
        >
          <Box>
            <SelectionChip
              onChange={(e: NotificationTabType) =>
                setNotificationTab(e as NotificationTabType)
              }
              value={notificationTab}
              options={Object.values(NotificationTabType).map((value) => ({
                value,
                label: `${t(`notificationsMenu.${value}`)} ${value === NotificationTabType.UNREAD && unreadTotal ? `(${unreadTotal})` : ''}`,
                dataTestId: `notification-tab-${value.toLowerCase()}`,
              }))}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              mr: 1,
              mb: 1,
            }}
          >
            <Chip
              small
              text={t('markAllAsRead')}
              onClick={() => {
                markAllRead();
                handleClose();
              }}
            />
          </Box>
        </Box>
        <Divider />
        {(notificationTab === NotificationTabType.ALL
          ? notificationLoading
          : loading) || custodianNotificationLoading ? (
          <Skeleton
            variant='rounded'
            height='100px'
            width='calc(100% - 32px)'
            sx={{ m: 2 }}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              paddingX: 2,
              pt: 2,
            }}
          >
            {Object.keys(groupedNotifications).map((key) => (
              <Box key={key}>
                <Typography
                  variant='labelLarge'
                  component='div'
                  color='textSecondary'
                  sx={{ mb: 1 }}
                >
                  {key === 'today' ? t('notificationsMenu.today') : key}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  {groupedNotifications[key].map((notification: any) => (
                    <Box key={notification.id}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          width: '100%',
                        }}
                      >
                        <Badge
                          invisible={notification.read}
                          variant='dot'
                          color='error'
                          sx={{ mr: 2, mt: 1.5 }}
                        ></Badge>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            whiteSpace: 'initial',
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant='bodyLarge'
                            sx={{ fontWeight: 600 }}
                          >
                            {translateBackend(notification.title)}
                          </Typography>
                          <Typography
                            variant='bodyMedium'
                            sx={{ fontWeight: 400 }}
                          >
                            {translateBackend(notification.body)}
                          </Typography>
                          <Typography
                            variant='bodySmall'
                            sx={{ color: theme.color.onSurfaceVariant }}
                          >
                            {key === 'today'
                              ? dayjs(notification.createdAt).fromNow()
                              : dayjs(notification.createdAt).format('h:mm a')}
                          </Typography>
                          <Box
                            sx={{
                              mt: 0.5,
                            }}
                          >
                            <Button
                              size='small'
                              variant='outlined'
                              onClick={() => {
                                if (!useCustodianNotifications) {
                                  markRead({
                                    variables: {
                                      input: {
                                        notificationId: notification.id,
                                      },
                                    },
                                    onCompleted: () => {
                                      window.location.href = notification.link;
                                    },
                                  });
                                  handleClose();
                                } else {
                                  const fullLink = `${window.location.origin}${notification.link}`;
                                  window.location.href = fullLink;
                                }
                              }}
                            >
                              {translateBackend(notification.linkText)}
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
            {!useCustodianNotifications && totalCount > PAGE_SIZE && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'end',
                  mr: 1,
                  mt: 1,
                }}
              >
                <Pagination
                  size='small'
                  count={Math.ceil(totalCount / PAGE_SIZE)}
                  page={page}
                  onChange={(_e, p) => setPage(p)}
                />
              </Box>
            )}
            {totalCount === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100px',
                }}
              >
                <Typography
                  variant='bodyMedium'
                  sx={{ color: theme.color.onSurfaceVariant, fontWeight: 500 }}
                >
                  {t('noNotifications')}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Menu>
    </>
  );
};
